import { useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import {
    LinearFilter,
    LinearSRGBColorSpace,
    Mesh,
    PerspectiveCamera,
    RGBAFormat,
    ShaderMaterial,
    UnsignedByteType,
    WebGLRenderTarget,
} from 'three'
import type { Camera, Scene, WebGLRenderer } from 'three'

import {
    registerWebGlCanvasCapture,
    type WebGlCaptureOptions,
} from './webglCanvasCapture'
import {
    resolveShareCardCropRect,
    scaleShareCardCropRect,
    type ShareCardScreenRect,
} from './shareCardScreenBounds'

const MAX_EXPORT_DIMENSION = 4096

function waitNextFrame(): Promise<void> {
    return new Promise((resolve) => {
        requestAnimationFrame(() => resolve())
    })
}

// Match three.js `LinearToSRGB` (display-referred linear → sRGB)
function linearNormToSrgbByte(linear01: number): number {
    const c = Math.min(1, Math.max(0, linear01))
    const srgb =
        c < 0.0031308
            ? c * 12.92
            : 1.055 * Math.pow(c, 1 / 2.4) - 0.055
    return Math.min(255, Math.max(0, Math.round(srgb * 255)))
}

// RT readback is linear RGBA8; the exported image expects sRGB-encoded 8-bit RGB
function linearDisplayRgbToSrgbBytesInPlace(buffer: Uint8Array): void {
    for (let i = 0; i < buffer.length; i += 4) {
        buffer[i] = linearNormToSrgbByte(buffer[i] / 255)
        buffer[i + 1] = linearNormToSrgbByte(buffer[i + 1] / 255)
        buffer[i + 2] = linearNormToSrgbByte(buffer[i + 2] / 255)
    }
}

// WebGL `readPixels` / RT readback uses bottom-left origin; 2D canvas is top-left
function flipRowsRgba(buffer: Uint8Array, width: number, height: number): void {
    const rowBytes = width * 4
    const temp = new Uint8Array(rowBytes)
    const half = Math.floor(height / 2)
    for (let y = 0; y < half; y++) {
        const top = y * rowBytes
        const bottom = (height - 1 - y) * rowBytes
        temp.set(buffer.subarray(top, top + rowBytes))
        buffer.copyWithin(top, bottom, bottom + rowBytes)
        buffer.set(temp, bottom)
    }
}

// JPEG fallback quality (0–1); ignored for the default PNG export
const JPEG_EXPORT_QUALITY = 0.92

function resolveExportDimensions(
    width: number,
    height: number,
    exportScale: number,
): { width: number; height: number; scaleX: number; scaleY: number } {
    const clampedScale = Math.max(1, exportScale)
    let exportWidth = Math.round(width * clampedScale)
    let exportHeight = Math.round(height * clampedScale)
    const maxDim = Math.max(exportWidth, exportHeight)

    if (maxDim > MAX_EXPORT_DIMENSION) {
        const shrink = MAX_EXPORT_DIMENSION / maxDim
        exportWidth = Math.round(exportWidth * shrink)
        exportHeight = Math.round(exportHeight * shrink)
    }

    return {
        width: Math.max(1, exportWidth),
        height: Math.max(1, exportHeight),
        scaleX: exportWidth / width,
        scaleY: exportHeight / height,
    }
}

function withShaderResolution(
    scene: Scene,
    width: number,
    height: number,
    render: () => void,
): void {
    const restores: Array<() => void> = []

    scene.traverse((object) => {
        if (!(object instanceof Mesh)) {
            return
        }

        const materials = Array.isArray(object.material)
            ? object.material
            : [object.material]

        for (const material of materials) {
            if (
                !(material instanceof ShaderMaterial) ||
                !material.uniforms.uResolution?.value
            ) {
                continue
            }

            const resolution = material.uniforms.uResolution.value
            const previousX = resolution.x
            const previousY = resolution.y
            resolution.set(width, height)
            restores.push(() => resolution.set(previousX, previousY))
        }
    })

    try {
        render()
    } finally {
        restores.forEach((restore) => restore())
    }
}

function withCameraAspect(
    camera: Camera,
    aspect: number,
    render: () => void,
): void {
    if (!(camera instanceof PerspectiveCamera)) {
        render()
        return
    }

    const previousAspect = camera.aspect
    camera.aspect = aspect
    camera.updateProjectionMatrix()

    try {
        render()
    } finally {
        camera.aspect = previousAspect
        camera.updateProjectionMatrix()
    }
}

function cropCanvas(
    source: HTMLCanvasElement,
    crop: ShareCardScreenRect,
): HTMLCanvasElement | null {
    const width = Math.max(1, Math.round(crop.right - crop.left))
    const height = Math.max(1, Math.round(crop.bottom - crop.top))
    const out = document.createElement('canvas')
    out.width = width
    out.height = height
    const ctx = out.getContext('2d')
    if (!ctx) {
        return null
    }
    ctx.drawImage(
        source,
        crop.left,
        crop.top,
        width,
        height,
        0,
        0,
        width,
        height,
    )
    return out
}

async function captureViewportToBlob(
    renderer: WebGLRenderer,
    scene: Scene,
    camera: Camera,
    getTarget: (w: number, h: number) => WebGLRenderTarget,
    mimeType: string,
    cropToShareCard: boolean,
    exportScale: number,
): Promise<Blob | null> {
    await waitNextFrame()

    const viewportWidth = renderer.domElement.width
    const viewportHeight = renderer.domElement.height
    if (viewportWidth <= 0 || viewportHeight <= 0) {
        console.warn('[WebGL capture] zero viewport')
        return null
    }

    const exportDimensions = resolveExportDimensions(
        viewportWidth,
        viewportHeight,
        exportScale,
    )
    const captureWidth = exportDimensions.width
    const captureHeight = exportDimensions.height

    const shareCardCrop = cropToShareCard
        ? resolveShareCardCropRect(camera, viewportWidth, viewportHeight)
        : null
    const scaledShareCardCrop =
        shareCardCrop != null
            ? scaleShareCardCropRect(
                  shareCardCrop,
                  exportDimensions.scaleX,
                  exportDimensions.scaleY,
              )
            : null

    const target = getTarget(captureWidth, captureHeight)
    const prevTarget = renderer.getRenderTarget()

    withCameraAspect(camera, captureWidth / captureHeight, () => {
        withShaderResolution(scene, captureWidth, captureHeight, () => {
            renderer.setRenderTarget(target)
            renderer.render(scene, camera)
            renderer.setRenderTarget(prevTarget)
        })
    })

    const buffer = new Uint8Array(captureWidth * captureHeight * 4)
    renderer.readRenderTargetPixels(
        target,
        0,
        0,
        captureWidth,
        captureHeight,
        buffer,
    )
    flipRowsRgba(buffer, captureWidth, captureHeight)
    linearDisplayRgbToSrgbBytesInPlace(buffer)

    const out = document.createElement('canvas')
    out.width = captureWidth
    out.height = captureHeight
    const ctx = out.getContext('2d')
    if (!ctx) {
        console.warn('[WebGL capture] 2D canvas context unavailable')
        return null
    }
    const imageData = ctx.createImageData(captureWidth, captureHeight)
    imageData.data.set(buffer)
    ctx.putImageData(imageData, 0, 0)

    const exportCanvas =
        scaledShareCardCrop != null
            ? cropCanvas(out, scaledShareCardCrop) ?? out
            : out

    return new Promise((resolve) => {
        exportCanvas.toBlob(
            (blob) => {
                resolve(blob)
            },
            mimeType,
            JPEG_EXPORT_QUALITY,
        )
    })
}

// Registers image capture via one offscreen full-viewport render (no `preserveDrawingBuffer`). Produces a PNG `Blob` by default. Mount inside `<Canvas>`
export function WebGlCaptureBridge() {
    const { gl, scene, camera } = useThree()
    const targetRef = useRef<WebGLRenderTarget | null>(null)

    useEffect(() => {
        const getTarget = (w: number, h: number) => {
            const cur = targetRef.current
            const needsRecreate = !cur || cur.width !== w || cur.height !== h
            if (needsRecreate) {
                cur?.dispose()
                const rt = new WebGLRenderTarget(w, h, {
                    minFilter: LinearFilter,
                    magFilter: LinearFilter,
                    format: RGBAFormat,
                    type: UnsignedByteType,
                    depthBuffer: true,
                    stencilBuffer: false,
                    // Linear color buffer (RGBA8): same tone mapping as the canvas via XR flag, but avoids SRGB8_ALPHA8 + readPixels mismatch (often “too bright”)
                    colorSpace: LinearSRGBColorSpace,
                })
                    // Plain RTs skip tone mapping; XR-style targets match the default buffer path
                    ; (rt as WebGLRenderTarget & { isXRRenderTarget: boolean }).isXRRenderTarget = true
                targetRef.current = rt
            }
            return targetRef.current!
        }

        const impl = async (opts?: WebGlCaptureOptions): Promise<Blob | null> => {
            try {
                return await captureViewportToBlob(
                    gl,
                    scene,
                    camera,
                    getTarget,
                    opts?.mimeType ?? 'image/png',
                    opts?.cropToShareCard ?? false,
                    opts?.exportScale ?? 1,
                )
            } catch (e) {
                console.warn('[WebGL capture] failed', e)
                return null
            }
        }

        registerWebGlCanvasCapture(impl)
        return () => {
            registerWebGlCanvasCapture(null)
            targetRef.current?.dispose()
            targetRef.current = null
        }
    }, [gl, scene, camera])

    return null
}
